import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { Modal } from "../../../common/Modal";
import {
  DocumentIcon,
  ShieldIcon,
} from "../../../../assets/ui/icons";
import {
  useGetCategoryListMutation,
  useDeleteCategoryMutation,
} from "../../../../store/Company/companyApiSlice";
import { uploadFilePath } from "../../../../constants/s3UploadPath";
import { CommonToast } from "../../../common/Toast";
import { SectionAddBar } from "./SectionAddBar";
import { EditSectionLayout } from "./EditSectionLayout";
import { EditFooterBar } from "./EditFooterBar";
import { MembershipVerifyNotice } from "./MembershipVerifyNotice";
import { DetailCard } from "./DetailCard";
import { TaxFormModal } from "./TaxFormModal";
import { VerificationModal } from "./VerificationModal";
import { TAX_ROWS, taxStatus } from "./taxFields";

/* Section 07 — Tax Information. */

const ROW_ICONS = {
  tax_category_title: DocumentIcon,
  tax_reg_no: ShieldIcon,
  upload_file: DocumentIcon,
};

/* Where this section's supporting documents live — the same path and category
   the tax dialog already uploads to. */
const VERIFY_SPEC = {
  uploadPath: uploadFilePath?.tax,
  category: "tax_document",
};

export const TaxInformationSection = ({
  onNext,
  onPrev,
  onSkip,
  header,
  className = "",
}) => {
  const { currentEditingCompany } = useSelector((state) => state.company);
  const { token } = useSelector((state) => state.auth);
  const companyID = currentEditingCompany?.companyID;

  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);

  const [getCategoryList, { isLoading }] = useGetCategoryListMutation();
  const [deleteCategory] = useDeleteCategoryMutation();

  const refresh = useCallback(async () => {
    if (!companyID) return;
    const res = await getCategoryList({ id: companyID, token });
    /* Transcribed: the refetch assigns whatever came back, so deleting the
       last row empties the list. */
    setRows(res?.data || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyID, token]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  /* Transcribed: a success toast, then the list is re-read. */
  const remove = async (record) => {
    const res = await deleteCategory({
      data: { tax_id: record?.tax_id, company_id: companyID },
      token,
    });
    if (res) {
      CommonToast.success("Tax Category Deleted Successfully");
      refresh();
    }
  };

  const openForm = (record) => {
    setEditing(record || null);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
  };

  const cardRows = (record) =>
    TAX_ROWS.map((row) => ({
      key: row.key,
      label: row.label,
      icon: ROW_ICONS[row.key],
      value:
        row.key === "upload_file" ? (
          record.upload_file ? (
            <a
              href={record.upload_file}
              target="_blank"
              rel="noopener noreferrer"
              className="underline decoration-[1.5px] underline-offset-[3px]"
            >
              View Document
            </a>
          ) : null
        ) : (
          /* Transcribed: the card strips a pipe before showing a value. */
          String(record[row.key] ?? "").replace("|", "")
        ),
    }));

  return (
    <EditSectionLayout
      className={className}
      header={header}
      footer={
        <EditFooterBar onBack={onPrev} onSkip={onSkip} onContinue={onNext} />
      }
    >
      <h2 className="m-0 text-[16px] font-bold text-[var(--ink)] font-sans">
        Tax Information
      </h2>

      {/* Members only — see MembershipVerifyNotice. The offer says the
          verification is free WITH YOUR MEMBERSHIP, so it is not shown to
          anyone who does not have one. */}
      <MembershipVerifyNotice onUpload={() => setVerifyOpen(true)} />

      {/* Transcribed: Add is always offered here, however many rows exist —
          unlike Address & Contact, which hides it once one exists. */}
      <SectionAddBar
        className="mt-4"
        prompt="Add New Tax Information"
        label="Add Tax Information"
        onAdd={() => openForm(null)}
      />

      {isLoading ? (
        <p
          role="status"
          aria-live="polite"
          className="mt-4 text-[13px] text-[var(--muted)] font-sans"
        >
          Loading Data…
        </p>
      ) : (
        rows.length > 0 && (
          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
            {rows.map((record) => (
              <DetailCard
                key={record.tax_id}
                status={taxStatus(record)}
                rows={cardRows(record)}
                onEdit={() => openForm(record)}
                onDelete={() => remove(record)}
              />
            ))}
          </div>
        )
      )}

      {formOpen && (
        <Modal modalHandle={closeForm} noClose bare>
          <TaxFormModal
            record={editing}
            onClose={closeForm}
            onSaved={() => {
              closeForm();
              refresh();
            }}
          />
        </Modal>
      )}

      {verifyOpen && (
        <Modal modalHandle={() => setVerifyOpen(false)} noClose bare>
          <VerificationModal
            spec={VERIFY_SPEC}
            onClose={() => setVerifyOpen(false)}
            onDone={() => {
              setVerifyOpen(false);
              refresh();
            }}
          />
        </Modal>
      )}
    </EditSectionLayout>
  );
};

export default TaxInformationSection;
